import React, {useEffect, useRef, useState} from 'react'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {Modal} from 'react-bootstrap'
import {FaTimesCircle} from 'react-icons/fa'
import {isValidPhoneNumber} from 'react-phone-number-input'
import PrimaryButton from '../common/buttons/PrimaryButton'

interface FormValues {
  first_name: string
  last_name: string
  phone_number: string
  address: string
}

interface StaffFormProps {
  isOpen: boolean
  onClose: () => void
  isLoading?: boolean
  handleSubmit: (values: any, resetForm: () => void) => void
  initialData?: any
}

const validationSchema = Yup.object({
  first_name: Yup.string()
    .trim()
    .test('matches-pattern', 'First name can only contain letters', (value) => {
      if (!value || value.trim() === '') return true
      return /^[A-Za-z ]+$/.test(value)
    })
    .test('min-length', 'First name must be at least 2 characters', (value) => {
      if (!value || value.trim() === '') return true
      return value.trim().length >= 2
    })
    .test('max-length', 'First name cannot exceed 100 characters', (value) => {
      if (!value) return true
      return value.length <= 100
    })
    .notRequired(),
  last_name: Yup.string()
    .trim()
    .test('matches-pattern', 'Last name can only contain letters', (value) => {
      if (!value || value.trim() === '') return true
      return /^[A-Za-z ]+$/.test(value)
    })
    .test('min-length', 'Last name must be at least 2 characters', (value) => {
      if (!value || value.trim() === '') return true
      return value.trim().length >= 2
    })
    .test('max-length', 'Last name cannot exceed 100 characters', (value) => {
      if (!value) return true
      return value.length <= 100
    })
    .notRequired(),
  phone_number: Yup.string()
    .notRequired()
    .test('is-valid-phone', 'Invalid phone number', (value) => {
      if (!value || value.trim() === '') return true // Allow empty
      return isValidPhoneNumber(value)
    }),
  address: Yup.string()
    .trim()
    .test('min-length', 'Address must be at least 2 characters', (value) => {
      if (!value || value.trim() === '') return true
      return value.trim().length >= 2
    })
    .test('max-length', 'Address must be at most 255 characters', (value) => {
      if (!value) return true
      return value.length <= 255
    })
    .notRequired(),
})

// Helper function to normalize phone number to E.164 format
const normalizePhoneToE164 = (phone: string | null | undefined): string => {
  if (!phone) return ''
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')
  // If it already starts with +, return as is
  if (cleaned.startsWith('+')) return cleaned
  // If it's all digits and not empty, add + prefix
  if (cleaned.length > 0) return `+${cleaned}`
  return ''
}

function StaffForm({isOpen, onClose, isLoading, handleSubmit, initialData}: StaffFormProps) {
  const resetFormRef = useRef<(() => void) | null>(null)
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [formDirty, setFormDirty] = useState(false)

  const initialValues: FormValues = {
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    phone_number: normalizePhoneToE164(initialData?.phone_number),
    address: initialData?.address || '',
  }

  const handleClose = () => {
    if (formDirty) {
      setShowDiscardModal(true)
    } else {
      onClose()
    }
  }

  const discardChanges = () => {
    resetFormRef.current?.()
    setFormDirty(false)
    setShowDiscardModal(false)
    onClose()
  }

  // reset form when user close and re-open the form
  useEffect(() => {
    if (isOpen && resetFormRef.current) {
      resetFormRef.current()
      setFormDirty(false)
    }
  }, [isOpen])

  return (
    <>
      {isOpen && (
        <div className='modal-backdrop show' style={{backgroundColor: 'rgba(0,0,0,0.5)'}} />
      )}

      <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} tabIndex={-1}>
        <div className='modal-dialog modal-dialog-centered modal-lg'>
          <div className='modal-content rounded-4 shadow'>
            <div className='modal-header bg-light border-bottom-0 mb-0'>
              <h5 className='modal-title fw-semibold'>Staff Form</h5>
              <button type='button' className='btn-close' onClick={handleClose}></button>
            </div>

            <div className='modal-body p-4'>
              <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={({...values}, {setSubmitting, resetForm}) => {
                  // Build payload with only non-empty fields (partial update)
                  const payload: any = {}
                  
                  if (values.first_name?.trim()) {
                    payload.first_name = values.first_name.trim()
                  }
                  
                  if (values.last_name?.trim()) {
                    payload.last_name = values.last_name.trim()
                  }
                  
                  if (values.phone_number?.trim()) {
                    const rawPhone = values.phone_number.replace(/[^\d+]/g, '')
                    if (rawPhone) {
                      payload.phone_number = rawPhone
                    }
                  }
                  
                  if (values.address?.trim()) {
                    payload.address = values.address.replace(/\s+/g, ' ').trim()
                  }

                  // Only submit if there's at least one field to update
                  if (Object.keys(payload).length === 0) {
                    // This shouldn't happen due to validation, but just in case
                    return
                  }

                  handleSubmit(payload, resetForm)
                  setFormDirty(false)
                }}
              >
                {({isSubmitting, values, setFieldValue, resetForm}) => {
                  resetFormRef.current = resetForm

                  return (
                    <Form onChange={() => setFormDirty(true)}>
                      <div className='mb-3'>
                        <div className='d-flex gap-3 mb-3'>
                          <div className='flex-grow-1'>
                            <label className='form-label'>
                              First Name
                            </label>
                            <Field
                              name='first_name'
                              className='form-control'
                              onInput={(e: any) => {
                                e.currentTarget.value = e.currentTarget.value.replace(
                                  /[^a-zA-Z\s]/g,
                                  ''
                                )
                              }}
                            />
                            <ErrorMessage
                              name='first_name'
                              component='div'
                              className='text-danger'
                            />
                          </div>

                          <div className='flex-grow-1'>
                            <label className='form-label'>
                              Last Name
                            </label>
                            <Field
                              name='last_name'
                              className='form-control'
                              onInput={(e: any) => {
                                e.currentTarget.value = e.currentTarget.value.replace(
                                  /[^a-zA-Z\s]/g,
                                  ''
                                )
                              }}
                            />
                            <ErrorMessage
                              name='last_name'
                              component='div'
                              className='text-danger'
                            />
                          </div>
                        </div>

                        <div className='mb-3'>
                          <div className='d-flex gap-3 mb-3'>
                            <div className='flex-grow-1'>
                              <label className='form-label'>
                                Email
                              </label>
                              <input
                                type='email'
                                className='form-control'
                                value={initialData?.email || ''}
                                readOnly
                                disabled
                                style={{backgroundColor: '#e9ecef', cursor: 'not-allowed'}}
                              />
                            </div>
                            <div className='flex-grow-1'>
                              <label className='form-label'>
                                Phone
                              </label>
                              <PhoneInput
                                defaultCountry='US'
                                international
                                value={values.phone_number || ''}
                                onChange={(value) => {
                                  setFieldValue('phone_number', value || '')
                                  setFormDirty(true)
                                }}
                                maxLength={15}
                                inputMode='tel'
                                className='form-control'
                              />
                              <ErrorMessage
                                name='phone_number'
                                component='div'
                                className='text-danger'
                              />
                            </div>
                          </div>
                        </div>

                        <div className='mb-3'>
                          <label className='form-label'>Address</label>
                          <Field name='address' as='textarea' className='form-control' />
                          <ErrorMessage
                            name='address'
                            component='div'
                            className='text-danger'
                          />
                        </div>
                      </div>

                      <div className='d-flex justify-content-end mt-5'>
                        <PrimaryButton type='submit' className='' disabled={isLoading || isSubmitting}>
                          {(isLoading || isSubmitting) ? (
                            <span className='d-flex align-items-center'>
                              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
                              Updating...
                            </span>
                          ) : (
                            'Update'
                          )}
                        </PrimaryButton>
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Discard Modal */}
      <Modal show={showDiscardModal} onHide={() => setShowDiscardModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTimesCircle className='text-danger me-2' />
            Unsaved Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have entered information that hasn't been saved yet. Are you sure you want to
          discard your changes?
        </Modal.Body>
        <Modal.Footer>
          <PrimaryButton color='secondary' onClick={() => setShowDiscardModal(false)}>
            Keep Editing
          </PrimaryButton>
          <PrimaryButton color='danger' onClick={discardChanges}>
            Discard Changes
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default StaffForm

